import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../layouts/AdminLayout';
import { Hotel } from '../types/hotel';
import { apiService } from '../services/api';
import Loader from '../components/Loader';
import ApprovalModal from '../components/ApprovalModal';
import {
  ArrowLeft,
  MapPin,
  Phone,
  Mail,
  Globe,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Edit2,
  Trash2,
  UserIcon,
  Hotel as HotelIcon,
} from 'lucide-react';
import { getAccommodationLabelById } from '../utils/helper';
import { MEDIA_URL } from '../config';

const HotelDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState<Hotel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [modalType, setModalType] = useState<'approve' | 'reject' | null>(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [regions, setRegions] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);


  console.log(hotel);

  // useEffect(() => {
  //   const fetchHotelDetails = async () => {
  //     if (!id) return;
  //     setIsLoading(true);
  //     try {
  //       const data = await apiService.getHotelById(id);
  //       console.log(data);
  //       let hotelData = data?.data;
  //       setHotel(hotelData);
  //       setError('');
  //     } catch (err) {
  //       setError(err instanceof Error ? err.message : 'Failed to fetch hotel details');
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   };

  //   if (id) {
  //     fetchHotelDetails();
  //   }
  //   setSelectedPhotoIndex(0);
  // }, [id]);

  useEffect(() => {
    const fetchHotelDetails = async () => {
      if (!id) return;

      setIsLoading(true);

      try {
        const [hotelRes, regionRes] = await Promise.all([
          apiService.getHotelById(id),
          apiService.getRegions()
        ]);

        const hotelData = hotelRes.data;

        setHotel(hotelData);
        setRegions(regionRes);

        if (hotelData.regionId) {
          const districtRes = await apiService.getDistricts(hotelData.regionId);
          setDistricts(districtRes);
        }

        setError("");

      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch hotel");
      } finally {
        setIsLoading(false);
      }
    };

    fetchHotelDetails();
  }, [id]);


  const handleApprove = async (reason?: string) => {
    if (!hotel) return;
    setIsProcessing(true);
    try {
      console.log('Approve hotel:', hotel.id);
      setModalType(null);
      const data = await apiService.approveHotel(hotel.id);
      setHotel({
        ...hotel,
        status: 2,
        verificationStatus: "approved",
        updatedAt: new Date().toISOString(),
      });
      //setHotel(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve hotel');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async (reason?: string) => {
    if (!hotel || !reason) return;
    setIsProcessing(true);
    try {
      console.log('Reject hotel:', hotel.id, 'Reason:', reason);
      setModalType(null);
      const data = await apiService.rejectHotel(hotel.id, reason);
      //setHotel(data);
      setHotel({
        ...hotel,
        status: 3,
        verificationStatus: "rejected",
        rejectionReason: reason,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject hotel');
    } finally {
      setIsProcessing(false);
    }
  };
  // const formatPhone = (phone?: string) => {
  //   if (!phone) return "N/A";

  //   let cleaned = phone.replace(/\D/g, "");

  //   if (cleaned.startsWith("0")) {
  //     cleaned = cleaned.slice(1);
  //   }

  //   return `+255${cleaned}`;
  // };

  const getRegionName = (id: any) => {
    const region = regions.find((r) => r.value == id);
    return region ? region.label : id;
  };

  const getDistrictName = (id: any) => {
    const district = districts.find((d) => d.value == id);
    return district ? district.label : id;
  };

  const formatPhone = (phone?: string) => {
    if (!phone) return "N/A";

    return phone.startsWith("+") ? phone : `+${phone}`;
  };


  const getStatusBadge = () => {
    if (!hotel) return null;
    switch (hotel.verificationStatus) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-full text-xs font-medium">
            <CheckCircle size={14} />
            Approved
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-warm-100 text-warm-700 border border-warm-300 rounded-full text-xs font-medium">
            <Clock size={14} />
            Pending Review
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-full text-xs font-medium">
            <XCircle size={14} />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Loader fullScreen />
      </AdminLayout>
    );
  }

  if (error || !hotel) {
    return (
      <AdminLayout>
        <div>
          <button
            onClick={() => navigate('/hotels')}
            className="mb-6 flex items-center gap-2 text-sm font-medium text-sage-600 hover:text-sage-900 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Hotels
          </button>
          <div className="bg-white rounded-xl border border-beige-300 p-12 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <AlertCircle size={24} className="text-red-500" />
            </div>
            <p className="text-sage-900 text-base font-semibold mb-1">{error || 'Hotel not found'}</p>
            <button
              onClick={() => navigate('/hotels')}
              className="mt-4 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-xl transition-colors"
            >
              Back to Hotels
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        {/* Back Button */}
        <button
          onClick={() => navigate('/hotels')}
          className="mb-6 flex items-center gap-2 text-sm font-medium text-sage-600 hover:text-sage-900 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Hotels
        </button>

        {/* Error */}
        {error && (
          <div className="mb-6 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Header Card */}
        <div className="bg-white rounded-xl border border-beige-300 overflow-hidden mb-6">
          <div className="bg-brand-700 text-white p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-3">
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-serif font-bold text-balance">{hotel.propName}</h1>
                <div className="flex items-center gap-2 text-brand-200 mt-1.5">
                  <MapPin size={14} />
                  <span className="text-sm">{hotel.address}</span>
                </div>
              </div>
              <div className="flex-shrink-0">{getStatusBadge()}</div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {/* <span className="px-2.5 py-1 bg-white/10 text-white rounded-lg text-xs font-medium capitalize">
                {getAccommodationLabelById((hotel as any).accommodationType || 1)}
              </span> */}
              <span className="px-2.5 py-1 bg-white/10 text-white rounded-lg text-xs font-medium">
                Region : {getRegionName((hotel as any).regionId)}, District : {getDistrictName((hotel as any).districtId)}
              </span>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 border-t border-beige-200">
            <div className="p-5 border-r border-beige-200">
              <p className="text-xs font-medium text-sage-400 uppercase tracking-wider">Base Price</p>
              <p className="text-xl font-bold text-sage-900 mt-1">
                {(hotel as any).basePrice ? `Tzs ${(hotel as any).basePrice}` : 'N/A'}
              </p>
              <p className="text-xs text-sage-500">per night</p>
            </div>
            <div className="p-5 border-r border-beige-200">
              <p className="text-xs font-medium text-sage-400 uppercase tracking-wider">Total Rooms</p>
              <p className="text-xl font-bold text-sage-900 mt-1">{hotel.totalRooms}</p>
              <p className="text-xs text-sage-500">{hotel.availableRooms} available</p>
            </div>
            <div className="p-5 border-r border-beige-200">
              <p className="text-xs font-medium text-sage-400 uppercase tracking-wider">Rating</p>
              <p className="text-xl font-bold text-sage-900 mt-1">{hotel.rating || '4'}</p>
            </div>
            <div className="p-5">
              <p className="text-xs font-medium text-sage-400 uppercase tracking-wider">Type</p>
              <p className="text-xl font-bold text-sage-900 mt-1">{getAccommodationLabelById((hotel as any).accommodationType || 1)}</p>
            </div>
          </div>
        </div>

        {/* Photo Gallery */}
        {hotel.photos && hotel.photos.length > 0 && (
          <div className="mb-6">
            <div className="w-full rounded-xl overflow-hidden">
              <img
                src={`${MEDIA_URL}${hotel.photos[selectedPhotoIndex].photo_url}`}
                alt={hotel.propName}
                className="w-full h-72 object-cover"
              />
            </div>
            <div className="flex gap-2 mt-2 overflow-x-auto">
              {hotel.photos.map((p, idx) => (
                <button
                  key={p.id + idx}
                  onClick={() => setSelectedPhotoIndex(idx)}
                  className={`flex-shrink-0 rounded-lg overflow-hidden w-20 h-12 border-2 transition-colors ${selectedPhotoIndex === idx ? 'border-brand-500' : 'border-beige-300'
                    }`}
                >
                  <img src={MEDIA_URL + p.photo_url} alt={`${hotel.propName}-${idx}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Left Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            {hotel.description && (
              <div className="bg-white rounded-xl border border-beige-300 p-6">
                <h2 className="text-sm font-semibold text-sage-900 uppercase tracking-wider mb-3">About</h2>
                <p className="text-sm text-sage-600 leading-relaxed">{hotel.description}</p>
              </div>
            )}

            {/* Contact Info */}
            {/* <div className="bg-white rounded-xl border border-beige-300 p-6">
              <h2 className="text-sm font-semibold text-sage-900 uppercase tracking-wider mb-4">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-beige-100 flex-shrink-0">
                    <Phone size={14} className="text-sage-500" />
                  </div>
                  <div>
                    <p className="text-xs text-sage-400 font-medium">Phone</p>
                    <p className="text-sm text-sage-900 mt-0.5">{hotel.ownerPhone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-beige-100 flex-shrink-0">
                    <Mail size={14} className="text-sage-500" />
                  </div>
                  <div>
                    <p className="text-xs text-sage-400 font-medium">Email</p>
                    <p className="text-sm text-sage-900 mt-0.5 break-all">{hotel.email}</p>
                  </div>
                </div>
                {hotel.website && (
                  <div className="flex items-start gap-3 md:col-span-2">
                    <div className="p-2 rounded-lg bg-beige-100 flex-shrink-0">
                      <Globe size={14} className="text-sage-500" />
                    </div>
                    <div>
                      <p className="text-xs text-sage-400 font-medium">Website</p>
                      <p className="text-sm text-brand-500 mt-0.5">{hotel.website}</p>
                    </div>
                  </div>
                )}
              </div>
            </div> */}
            <div className="bg-white rounded-xl border border-beige-300 p-6">
              <h2 className="text-sm font-semibold text-sage-900 uppercase tracking-wider mb-4">
                Contact Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* Contact Person Name */}
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-beige-100">
                    <UserIcon size={14} className="text-sage-500" />
                  </div>
                  <div>
                    <p className="text-xs text-sage-400 font-medium">Contact Person</p>
                    <p className="text-sm text-sage-900 mt-0.5">
                      {hotel.contactName || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Contact Phone */}
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-beige-100">
                    <Phone size={14} className="text-sage-500" />
                  </div>
                  <div>
                    <p className="text-xs text-sage-400 font-medium">Contact Phone</p>
                    <p className="text-sm text-sage-900 mt-0.5">
                      {formatPhone(hotel.phone)}
                    </p>
                  </div>
                </div>

                {/* Email */}
                {hotel.email && (
                  <div className="flex items-start gap-3 md:col-span-2">
                    <div className="p-2 rounded-lg bg-beige-100">
                      <Mail size={14} className="text-sage-500" />
                    </div>
                    <div>
                      <p className="text-xs text-sage-400 font-medium">Email</p>
                      <p className="text-sm text-sage-900 mt-0.5 break-all">
                        {hotel.email}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Location */}
            {/* <div className="bg-white rounded-xl border border-beige-300 p-6">
              <h2 className="text-sm font-semibold text-sage-900 uppercase tracking-wider mb-4">Location</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-beige-50 p-4 rounded-xl">
                  <p className="text-xs text-sage-400 font-medium">Region</p>
                  <p className="text-sm font-medium text-sage-900 mt-1">{hotel.address}</p>
                </div>
                <div className="bg-beige-50 p-4 rounded-xl">
                  <p className="text-xs text-sage-400 font-medium">District</p>
                  <p className="text-sm font-medium text-sage-900 mt-1">{hotel.district}</p>
                </div>
                <div className="bg-beige-50 p-4 rounded-xl col-span-2">
                  <p className="text-xs text-sage-400 font-medium">Full Address</p>
                  <p className="text-sm font-medium text-sage-900 mt-1">
                    {hotel.address}, {hotel.city}, {hotel.country}
                  </p>
                </div>
              </div>
            </div> */}

            {/* Amenities */}
            {/* {hotel.amenities && hotel.amenities.length > 0 && (
              <div className="bg-white rounded-xl border border-beige-300 p-6">
                <h2 className="text-sm font-semibold text-sage-900 uppercase tracking-wider mb-3">Amenities</h2>
                <div className="flex flex-wrap gap-2">
                  {hotel.amenities.map((amenity) => (
                    <span
                      key={amenity}
                      className="px-3 py-1.5 bg-brand-50 text-brand-600 border border-brand-200 rounded-lg text-xs font-medium"
                    >
                      {amenity}
                    </span>
                  ))}
                </div>
              </div>
            )} */}

            {/* Pricing */}
            <div className="bg-white rounded-xl border border-beige-300 p-6">
              <h2 className="text-sm font-semibold text-sage-900 uppercase tracking-wider mb-4">Pricing & Rules</h2>
              <div className="space-y-4">
                <div className="bg-warm-50 border border-warm-200 p-4 rounded-xl">
                  <p className="text-xs text-warm-600 font-medium">Base Price Per Night</p>
                  <p className="text-2xl font-bold text-sage-900 mt-1">
                    {(hotel as any).basePrice ? `Tzs ${(hotel as any).basePrice}` : 'N/A'}
                  </p>
                </div>
                {((hotel as any).availabilityRules || (hotel as any).pricingRules) && (
                  <div>
                    <p className="text-xs text-sage-400 font-medium mb-2">Availability Rules</p>
                    <p className="text-sm text-sage-600 bg-beige-50 p-3 rounded-xl leading-relaxed">
                      {(hotel as any).availabilityRules || (hotel as any).pricingRules}
                    </p>
                  </div>
                )}
                {(hotel as any).pricingPackages && (hotel as any).pricingPackages.length > 0 && (
                  <div>
                    <p className="text-xs text-sage-400 font-medium mb-2">Pricing Packages</p>
                    <div className="space-y-2">
                      {(hotel as any).pricingPackages.map((pkg: any, idx: number) => (
                        <div key={idx} className="border border-beige-300 rounded-xl p-4 flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-sage-900">{pkg.name}</p>
                            {pkg.features && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {pkg.features.map((f: string, fi: number) => (
                                  <span key={fi} className="text-xs text-sage-500 bg-beige-100 px-2 py-0.5 rounded-md">
                                    {f}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <p className="text-base font-bold text-brand-500 flex-shrink-0">${pkg.price}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Room Types */}
          {(hotel as any).rooms && (hotel as any).rooms.length > 0 && (
            <div className="bg-white rounded-xl border border-beige-300 p-6">
              <h2 className="text-sm font-semibold text-sage-900 uppercase tracking-wider mb-4">
                Room Types
              </h2>

              <div className="space-y-3">
                {(hotel as any).rooms.map((room: any, index: number) => (
                  <div
                    key={index}
                    className="flex items-center justify-between border border-beige-200 rounded-xl p-4"
                  >
                    <div className="flex items-center gap-2">
                      <HotelIcon size={16} className="text-sage-400" />
                      <span className="text-sm font-medium text-sage-900 capitalize">
                        {room.roomType}
                      </span>
                    </div>

                    <span className="text-sm font-semibold text-brand-600">
                      TZS {room.price} / night
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Owner Info */}
            {/* <div className="bg-white rounded-xl border border-beige-300 p-6">
              <h2 className="text-sm font-semibold text-sage-900 uppercase tracking-wider mb-4">Owner</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-sage-400 font-medium">Name</p>
                  <p className="text-sm font-medium text-sage-900 mt-0.5">{hotel.ownerName}</p>
                </div>
                <div className="flex items-center gap-2 text-sm text-sage-600">
                  <Phone size={14} className="text-sage-400" />
                  <span>{hotel.ownerPhone}</span>
                </div>
                {hotel.ownerEmail && (
                  <div className="flex items-center gap-2 text-sm text-sage-600">
                    <Mail size={14} className="text-sage-400" />
                    <span className="break-all text-xs">{hotel.ownerEmail}</span>
                  </div>
                )}
              </div>
            </div> */}

            {/* Verification */}
            <div className="bg-white rounded-xl border border-beige-300 p-6">
              <h2 className="text-sm font-semibold text-sage-900 uppercase tracking-wider mb-4">Verification</h2>
              <div className="mb-3">{getStatusBadge()}</div>
              {hotel.status === 2 && hotel.updatedAt && (
                <p className="text-xs text-sage-500 mt-2">
                  Approved on {new Date(hotel.updatedAt).toLocaleDateString()}
                  {/* {hotel.verifiedBy && <span> by {hotel.verifiedBy}</span>} */}
                </p>
              )}
              {hotel.status === 3 && hotel.rejectionReason && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-sage-500 mb-1.5">Rejection Reason</p>
                  <p className="text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded-xl leading-relaxed">
                    {hotel.rejectionReason}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {hotel.status === 1 && (
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => setModalType('approve')}
              disabled={isProcessing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white font-semibold text-sm rounded-xl transition-colors"
            >
              <CheckCircle size={16} />
              Approve Listing
            </button>
            <button
              onClick={() => setModalType('reject')}
              disabled={isProcessing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-semibold text-sm rounded-xl transition-colors"
            >
              <XCircle size={16} />
              Reject Listing
            </button>
          </div>
        )}

        {hotel.status !== 1 && (
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate(`/properties/edit/${hotel.id}`)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-brand-700 hover:bg-brand-800 text-white font-semibold text-sm rounded-xl transition-colors"
            >
              <Edit2 size={16} />
              Edit Details
            </button>

            <button
              onClick={() => { }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-red-300 text-red-600 hover:bg-red-50 font-semibold text-sm rounded-xl transition-colors"
            >
              <Trash2 size={16} />
              Delete Listing
            </button>
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {hotel && (
        <ApprovalModal
          isOpen={modalType !== null}
          hotelName={hotel.propName}
          type={modalType || 'approve'}
          onConfirm={modalType === 'approve' ? handleApprove : handleReject}
          onCancel={() => setModalType(null)}
          isLoading={isProcessing}
        />
      )}
    </AdminLayout>
  );
};

export default HotelDetails;
